import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  TextField,
  Collapse,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  makeStyles,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useQueryClient } from 'react-query'
import getOrgPlayers from '../../Apis/player/getOrgPlayers'
import postPlayerMatch from '../../Apis/stats/postPlayerMatch'
import { useCmsScope } from '../../context/useCmsScope'
import QueryKeys from '../../utils/constants/queryKeys'
import SeasonTeamPlayerCreateDrawer from '../PlayersPage/SeasonTeamPlayerCreateDrawer'

const useStyles = makeStyles({
  textField: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.35)', // Keep original border color on focus
      },
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: 'rgba(0, 0, 0, 0.35)', // Keep original label color on focus
    },
  },
})

const splitFullName = (fullName) => {
  const trimmed = (fullName || '').trim()
  if (!trimmed) return { firstName: '', lastName: '' }
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

const PlayerMatchingDropdown = ({
  isOpen,
  gameTeamPlayerId,
  eventTeamId,
  seasonTeamId,
  gameId,
  excludePlayerIds,
  onClose,
  playerFullName,
  playerJerseyNumber,
}) => {
  const classes = useStyles()
  // The game's org: matching is the org's own CMS work, never a paired federation's.
  const { orgId } = useCmsScope()
  const queryClient = useQueryClient()
  const [filteredPlayers, setFilteredPlayers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasMorePages, setHasMorePages] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingSelection, setPendingSelection] = useState(null)
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)

  // Fetch team players when dropdown is opened
  useEffect(() => {
    if (isOpen && eventTeamId) {
      setLoading(true)
      getOrgPlayers({ eventTeamId: eventTeamId, perPage: 10 })
        .then((response) => {
          const players = response?.players || []
          const metadata = response?.metadata || {}
          // Filter out already matched players
          const availablePlayers = players.filter(p => !excludePlayerIds.includes(p.id))
          setFilteredPlayers(availablePlayers)
          // Check if there are more pages
          setHasMorePages((metadata.totalPages || 1) > 1)
          setLoading(false)
        })
        .catch((error) => {
          console.error('Error fetching team players:', error)
          setLoading(false)
        })
    }
  }, [isOpen, eventTeamId, excludePlayerIds])

  // Debounced search - fetch players from API with search query
  useEffect(() => {
    if (!isOpen || !eventTeamId) return

    const delayDebounce = setTimeout(() => {
      setLoading(true)
      getOrgPlayers({ eventTeamId: eventTeamId, Q: searchTerm || undefined, perPage: 10 })
        .then((response) => {
          const players = response?.players || []
          const metadata = response?.metadata || {}
          // Filter out already matched players
          const availablePlayers = players.filter(p => !excludePlayerIds.includes(p.id))
          setFilteredPlayers(availablePlayers)
          // Check if there are more pages
          setHasMorePages((metadata.totalPages || 1) > 1)
          setLoading(false)
        })
        .catch((error) => {
          console.error('Error fetching team players:', error)
          setLoading(false)
        })
    }, 500)

    return () => clearTimeout(delayDebounce)
  }, [searchTerm, isOpen, eventTeamId, excludePlayerIds])

  const handlePlayerSelect = (selectedPlayer) => {
    setPendingSelection(selectedPlayer)
    setConfirmDialogOpen(true)
  }

  const handleConfirmMatch = async () => {
    if (!pendingSelection) return

    setConfirmDialogOpen(false)
    setLoading(true)
    try {
      await postPlayerMatch({
        orgId,
        gameId,
        gameTeamPlayerId: gameTeamPlayerId,
        playerId: pendingSelection.id
      })
      console.log('Successfully matched player:', pendingSelection.id, 'to game player:', gameTeamPlayerId)

      // Refetch the game stats query without invalidating (preserves scroll position)
      await queryClient.refetchQueries([QueryKeys.GET_GAME_STATS, gameId], { active: true })

      // Close the dropdown and reset state
      setSearchTerm('')
      setPendingSelection(null)
      onClose()
    } catch (error) {
      console.error('Error matching player:', error)
      setErrorMessage(error?.response?.data?.message || 'Failed to link player. Please try again.')
      setErrorSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelMatch = () => {
    setConfirmDialogOpen(false)
    setPendingSelection(null)
  }

  const handleCloseErrorSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setErrorSnackbarOpen(false)
  }

  const handlePlayerCreated = async (createdPlayer) => {
    if (!createdPlayer?.id) {
      setErrorMessage('Player created but no id was returned. Please link manually.')
      setErrorSnackbarOpen(true)
      return
    }
    setLoading(true)
    try {
      await postPlayerMatch({
        orgId,
        gameId,
        gameTeamPlayerId: gameTeamPlayerId,
        playerId: createdPlayer.id,
      })
      await queryClient.refetchQueries([QueryKeys.GET_GAME_STATS, gameId], { active: true })
      setSearchTerm('')
      onClose()
    } catch (error) {
      console.error('Error linking newly created player:', error)
      setErrorMessage(error?.response?.data?.message || 'Player created but failed to link. Please link manually.')
      setErrorSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const createDrawerInitialValues = (() => {
    const { firstName, lastName } = splitFullName(playerFullName)
    return {
      firstName,
      lastName,
      jerseyNumber: playerJerseyNumber != null ? String(playerJerseyNumber) : '',
    }
  })()

  return (
    <>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box
          margin={2}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
            padding: '16px',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box display="flex" alignItems="center" marginBottom={2}>
            <TextField
              label="Search players"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '250px' }}
              className={classes.textField}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setCreateDrawerOpen(true)}
              style={{ marginLeft: 16 }}
              disabled={!seasonTeamId || loading}
              title={!seasonTeamId ? 'This game is not linked to a season; cannot create a team player here.' : undefined}
            >
              Create Team Player
            </Button>
            {loading && (
              <Box marginLeft={2}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
          <Box style={{
            maxHeight: 300,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#fafafa'
          }}>
            {filteredPlayers.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding={4}
                style={{ color: '#888' }}
              >
                No matches
              </Box>
            ) : (
              <Table size="small">
                <TableBody>
                  {filteredPlayers.map((teamPlayer) => (
                    <TableRow
                      hover
                      key={teamPlayer.id}
                      onClick={() => handlePlayerSelect(teamPlayer)}
                      style={{ cursor: 'pointer', backgroundColor: '#ffffff' }}
                    >
                      <TableCell style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        {teamPlayer.personalData?.firstName || ''} {teamPlayer.personalData?.lastName || ''}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          padding: '12px 16px',
                          color: '#888',
                          width: '80px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}
                      >
                        {teamPlayer.teams?.[0]?.jerseyNumber ? `#${teamPlayer.teams[0].jerseyNumber}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {hasMorePages && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        align="center"
                        style={{
                          padding: '12px 16px',
                          color: '#888',
                          fontStyle: 'italic',
                          backgroundColor: '#fafafa',
                          fontSize: '1.2rem'
                        }}
                      >
                        ...
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
      </Collapse>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelMatch}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Player Match
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {pendingSelection && (
              <>
                Are you sure you want to link this game player to{' '}
                <strong>
                  {pendingSelection.personalData?.firstName || ''}{' '}
                  {pendingSelection.personalData?.lastName || ''}
                </strong>
                ? This action is irreversible.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelMatch} color="default">
            Cancel
          </Button>
          <Button onClick={handleConfirmMatch} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      <SeasonTeamPlayerCreateDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        seasonTeamId={seasonTeamId}
        initialValues={createDrawerInitialValues}
        onCreated={handlePlayerCreated}
      />
    </>
  )
}

PlayerMatchingDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  gameTeamPlayerId: PropTypes.string.isRequired,
  eventTeamId: PropTypes.string,
  seasonTeamId: PropTypes.string,
  gameId: PropTypes.string.isRequired,
  excludePlayerIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  playerFullName: PropTypes.string,
  playerJerseyNumber: PropTypes.number,
}

export default PlayerMatchingDropdown
